import { useMemo, useState } from "react"
import { Flex, Text, Card, Divider } from "components/common"
import styles from "./index.module.less"
import { t } from "i18next"
import dayjs from "dayjs"
import { LineChart } from "components/charts"
import theme from "utils/themeVars"
import { useGetClientEngagementScoresQuery } from "features/clients/detail/api"

const DATE_SPAN_DAYS_BY_LABEL = {
  "7D": 7,
  "30D": 30,
  "60D": 60,
  "90D": 90,
  "180D": 180,
}

const TREND_LINE_COLOR = "#17a2b8"

const POSITIVE_DELTA_COLOR = "#52c41a"

const EngagementTrendInsights = ({ clientId }) => {
  const [selectedDateSpanLabel, setSelectedDateSpanLabel] = useState("7D")

  const scoresQueryDates = useMemo(() => {
    const daySpan = DATE_SPAN_DAYS_BY_LABEL?.[selectedDateSpanLabel]
    const windowEndDay = dayjs().subtract(1, "day")
    const windowStartDay = windowEndDay.subtract(daySpan - 1, "day")
    return {
      startDate: windowStartDay.format("YYYY-MM-DD"),
      endDate: windowEndDay.format("YYYY-MM-DD"),
    }
  }, [selectedDateSpanLabel])

  const { data: engagementScoresDataset, isFetching: fetchingEngagementScores } = useGetClientEngagementScoresQuery(
    { clientId, ...scoresQueryDates },
    { skip: !clientId },
  )

  const trendLegendLabel = t("Engagement Trend")

  const chartData = useMemo(() => {
    const dailyPoints = engagementScoresDataset?.trendDailyPoints ?? []
    return dailyPoints.map(point => ({
      ...point,
      series: trendLegendLabel,
    }))
  }, [engagementScoresDataset, trendLegendLabel])

  const chartConfig = {
    data: chartData,
    xField: "dateLabel",
    yField: "score",
    seriesField: "series",
    color: [TREND_LINE_COLOR],
    smooth: true,
    height: 300,
    lineStyle: { stroke: TREND_LINE_COLOR, lineWidth: 2 },
    yAxis: {
      min: 0,
      max: 100,
      tickInterval: 20,
      grid: {
        line: {
          style: { stroke: "#f0f0f0", lineWidth: 1 },
        },
      },
      label: { style: { fill: "#8c8c8c" } },
    },
    xAxis: {
      label: { style: { fill: "#8c8c8c" } },
      line: { style: { stroke: "#ebebeb" } },
    },
    legend: {
      position: "bottom",
      marker: { symbol: "line" },
    },
    tooltip: {
      customContent: (_, tooltipItems) => {
        if (!tooltipItems?.length) return null
        const firstRow = tooltipItems[0]?.data
        const deltaVsPrior = firstRow?.percentageChange
        const deltaIsNumeric = typeof deltaVsPrior === "number"
        let deltaLabel = ""
        let deltaHue = theme["gray600"] || "#8c8c8c"
        if (deltaIsNumeric) {
          if (deltaVsPrior > 0) {
            deltaLabel = ` (+ ${deltaVsPrior}%)`
            deltaHue = POSITIVE_DELTA_COLOR
          } else if (deltaVsPrior < 0) {
            deltaLabel = ` (- ${Math.abs(deltaVsPrior)}%)`
            deltaHue = theme["error-color"]
          } else {
            deltaLabel = " (0%)"
          }
        }

        return (
          <Card size="small" bordered={false} className={styles.engagementTrendTooltip}>
            <Flex justify="space-between" align="flex-start" gap={8}>
              <Text className="fs12 bold">{t("AI Score")}</Text>
              <Flex align="baseline" gap={0} wrap="wrap" justify="flex-end">
                <Text className="fs12 bold">{firstRow?.score}%</Text>
                {deltaIsNumeric ? (
                  <Text className="fs12" style={{ color: deltaHue }}>
                    {deltaLabel}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
            <Divider className={styles.engagementTrendTooltipDivider} />
            <Flex vertical gap={2}>
              <Text className="fs12 bold">{t("Staff Member")}</Text>
              <Text className="fs12" type="secondary">
                {firstRow?.staffName || "—"}
              </Text>
            </Flex>
          </Card>
        )
      },
    },
  }

  return (
    <div className={styles.trendChartWrap}>
      <div className={styles.trendRangeBar}>
        {Object.keys(DATE_SPAN_DAYS_BY_LABEL).map(spanLabel => (
          <div
            key={spanLabel}
            className={`${styles.trendRangeItem} ${selectedDateSpanLabel === spanLabel ? styles.trendRangeItem_active : ""}`}
            onClick={() => setSelectedDateSpanLabel(spanLabel)}
            role="presentation"
          >
            {spanLabel}
          </div>
        ))}
      </div>

      <LineChart
        {...chartConfig}
        loading={fetchingEngagementScores}
        disabled={fetchingEngagementScores}
        animation={false}
      />
    </div>
  )
}

export default EngagementTrendInsights
